/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useRef, useState } from 'react'

import useIsVisible from '@Hooks/useIsVisible'
import ArrowDownBlue from '@Icons/arrow-down-blue.svg?react'
import ArrowUpBlue from '@Icons/arrow-up-blue.svg?react'
import Refresh from '@Icons/refresh.svg?react'
import { useLazyCalculateSmartPriceForProductQuery } from '@Store/api/dynamic-pricing'

import PositionBar from './PositionBar'
import ProductCardIndexTable from './ProductCardIndexTable'
import ProductCardPriceInfo from './ProductCardPriceInfo'
import ProductCardProductInfo from './ProductCardProductInfo'
import ProductCompetitionTable from './ProductCompetitionTable'
import { useMePreferencesQuery, useMeQuery } from '../../store/api'
import { useAppSelector } from '../../store/hooks'
import type { Product } from '../../store/types/product'

type Props = {
  product: Product
  isInFreezePeriod?: boolean
}

function Row({ product, isInFreezePeriod = false }: Props): JSX.Element {
  const ref = useRef<HTMLDivElement>(null)
  const isVisible = useIsVisible({ ref, once: true })

  const showProductList = useAppSelector(
    (state) => state.settings.show_product_competition_list,
  )
  const [isFooterExpanded, setIsFooterExpanded] = useState(showProductList)

  const { data: me } = useMeQuery()
  const { data: mePreferences } = useMePreferencesQuery()

  const [
    calculateSmartPrice,
    {
      data: smartprice,
      isLoading: isSmartpriceLoading,
      isUninitialized: isSmartpriceUninitialized,
      isSuccess: isSmartpriceSuccess,
      isError: isSmartpriceError,
    },
  ] = useLazyCalculateSmartPriceForProductQuery()

  const fetchSmartPrice = async (): Promise<void> => {
    await calculateSmartPrice(product.id)
  }

  const toggleExpandFooter = (): void => {
    setIsFooterExpanded((current: typeof showProductList) => !current)
  }

  useEffect(() => {
    setIsFooterExpanded(showProductList)
  }, [showProductList])

  useEffect(() => {
    if (!isVisible) {
      return
    }
    fetchSmartPrice()
  }, [isVisible])

  return (
    <div
      ref={ref}
      className="product-card flex flex-col border-2 border-solid border-gray-200 rounded-lg shadow mt-8 pb-4"
    >
      <div className="flex flex-col md:flex-row w-full container pt-6 pb-4">
        <div className="w-full md:w-2/3 flex flex-col flex-grow px-5 md:border-r border-r-0">
          <ProductCardProductInfo
            product={product}
            currency={me?.currency_name}
            registerSource={me?.register_source}
            packageType={me?.package_type}
            monitoringSource={me?.monitoring_source}
          />
        </div>
        <div className="w-full flex flex-col md:w-1/3 mt-4 md:mt-0">
          <div className="w-full h-[164px] flex flex-col px-3 xxl:px-5">
            {showProductList ? (
              <div className="flex flex-col gap-y-2 md:gap-y-5 items-center h-full border-b">
                <PositionBar position={product.position?.myIsCheap} />
              </div>
            ) : (
              <ProductCardIndexTable product={product} />
            )}
          </div>
          <div className="flex-1 w-full px-5 min-h-[136px]">
            <div className="flex flex-col pt-2 gap-y-2 md:gap-y-5 items-center text-center h-full">
              {isSmartpriceError ? (
                <div className="flex w-full justify-center py-3">
                  <button
                    type="button"
                    onClick={fetchSmartPrice}
                    className="border-none rounded-lg w-full flex justify-center items-center py-1.5 px-0 font-bold text-sm text-primary-600"
                  >
                    <Refresh className="mr-1 w-4 h-4" />
                    Retrieve smart price
                  </button>
                </div>
              ) : (
                <ProductCardPriceInfo
                  productId={product.id}
                  smartprice={smartprice}
                  isInFreezePeriod={isInFreezePeriod}
                  isLoading={
                    isSmartpriceLoading ||
                    isSmartpriceUninitialized ||
                    !isSmartpriceSuccess
                  }
                />
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="flex flex-col ">
        {isFooterExpanded && (
          <div className="lg:px-4 pb-3 flex flex-col">
            <ProductCompetitionTable
              product={product}
              totalPrice={mePreferences?.total_price}
              monitoringSource={mePreferences?.monitoring_source}
            />
          </div>
        )}
        <div className="w-full flex flex-row pt-4 pl-4 border-t-2 border-gray-200">
          <button
            className="flex flex-row"
            onClick={toggleExpandFooter}
            type="button"
          >
            <span className="font-medium text-sm text-primary-600 pr-2">
              {isFooterExpanded ? 'Hide' : 'Show'} the product competition
            </span>
            {isFooterExpanded ? <ArrowUpBlue /> : <ArrowDownBlue />}
          </button>
        </div>
      </div>
    </div>
  )
}

export default Row
